import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@mui/material'
import { BaseRouteParams, useNavigation } from 'hocs'
import { DateRange, dateRangeFromQuery, dateRangeToQuery } from 'dateRanges'
import DateRangePicker from 'uiComponents/popups/comparisonDateRangePicker'
import { MultiSelectCombobox, Option } from 'uiComponents/combobox'
import { PageTitle } from 'uiComponents/typography'
import { PlainLoader } from 'uiComponents/loaders'
import { InfiniteTable } from 'uiComponents/table/reactTable/infiniteTable'
import { TableColumn } from 'uiComponents/table/reactTable/interface'
import { LucideIcon } from 'utils/lucideIcon'
import { useLazyListAuditLogEntriesQuery, useListAuditLogActorsQuery } from './reduxQuery'
import { AuditLogEntry, OBJECT_TYPE_LABELS, SOURCE_LABELS, STAFF_MASK } from './schema'
import { ChangesCell, WhatCell, WhenCell, WhoCell } from './entryRow'
import { objectPath } from './links'
import './styles.scss'

const PAGE_SIZE = 25

const cursorFromLink = (link?: string | null) => (link ? new URL(link).searchParams.get('cursor') : null)

const listFromQuery = (value?: string) => (value ? value.split(',') : [])

const listToQuery = (value: string | string[] | null) => (Array.isArray(value) && value.length ? value.join(',') : null)

export const AuditLogPage = () => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const navigation = useNavigation()
    const latestNavigation = useRef(navigation)
    latestNavigation.current = navigation
    const query = navigation.query()
    const dateRange = dateRangeFromQuery(query, 'last30days')
    const filtersActive = !!(query.objectType || query.actor || query.source || query.objectId)

    const [listEntries, { isFetching, data, error, originalArgs }] = useLazyListAuditLogEntriesQuery()
    const [allData, setAllData] = useState<AuditLogEntry[]>([])
    const from = dateRange.from.toISOString()
    const to = dateRange.to.toISOString()

    const selected = {
        objectType: listFromQuery(query.objectType),
        actor: listFromQuery(query.actor),
        source: listFromQuery(query.source),
    }

    const baseParams = {
        accountSlug,
        from,
        to,
        ...selected,
        objectId: query.objectId,
        pageSize: PAGE_SIZE,
    }

    const loadData = () => {
        setAllData([])
        listEntries(baseParams)
    }

    const loadMore = useCallback(() => {
        const cursor = cursorFromLink(data?.next)
        if (cursor && !error) {
            listEntries({ ...baseParams, cursor })
        }
    }, [data?.next, error, accountSlug, from, to, query.objectType, query.objectId, query.actor, query.source])

    useEffect(loadData, [accountSlug, from, to, query.objectType, query.objectId, query.actor, query.source])

    useEffect(() => {
        if (!data?.results) return
        setAllData((prev) => (originalArgs?.cursor ? [...prev, ...data.results] : data.results))
    }, [data?.results])

    const retry = () => {
        const cursor = cursorFromLink(data?.next)
        if (allData.length && cursor) listEntries({ ...baseParams, cursor })
        else loadData()
    }

    const actors = useListAuditLogActorsQuery(accountSlug, { refetchOnMountOrArgChange: true })

    const setFilter = (q: Record<string, string | null>) => navigation.addQueryWithReplace(q)
    const onDateRangeChanged = (range: DateRange) => setFilter(dateRangeToQuery(range))
    const selectOptions = (labels: Record<string, string>): Option[] =>
        Object.entries(labels).map(([value, label]) => ({ value, label }))
    const actorOptions: Option[] = (actors.data ?? []).map((a) => ({
        value: a,
        label: a === STAFF_MASK ? 'Convious staff' : a,
    }))

    const setObject = useCallback((objectId: string) => latestNavigation.current.addQueryWithReplace({ objectId }), [])

    const columns = useMemo<TableColumn<AuditLogEntry>[]>(
        () => [
            {
                Header: 'When',
                accessor: 'createdAt',
                width: '10%',
                disableSortBy: true,
                Cell: ({ value }) => <WhenCell createdAt={value} />,
            },
            {
                Header: 'Who',
                accessor: 'actor',
                width: '20%',
                disableSortBy: true,
                className: 'overflow-allowed',
                Cell: ({ row: { original } }) => <WhoCell entry={original} />,
            },
            {
                Header: 'What',
                accessor: 'objectName',
                width: '30%',
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <WhatCell
                        entry={original}
                        objectPath={objectPath(original, accountSlug)}
                        onSelectObject={setObject}
                    />
                ),
            },
            {
                Header: 'Changes',
                id: 'changes',
                accessor: 'stateAfter',
                width: '40%',
                disableSortBy: true,
                className: 'overflow-allowed',
                Cell: ({ row: { original } }) => <ChangesCell entry={original} />,
            },
        ],
        [accountSlug],
    )

    const initialLoading = isFetching && allData.length === 0 && !error
    const isEmpty = !isFetching && !error && !!data && allData.length === 0

    return (
        <div className="audit-log-page">
            <PageTitle>Activity log</PageTitle>
            <p className="audit-log-subtitle">
                Who changed your venue&apos;s settings, and what changed. Times shown in your browser timezone.
            </p>

            <div className="audit-log-filters">
                <MultiSelectCombobox
                    className="audit-filter audit-filter-actor"
                    options={actorOptions}
                    searchPlaceholder="Anyone"
                    value={selected.actor}
                    onChange={(v) => setFilter({ actor: listToQuery(v) })}
                    loading={actors.isLoading}
                    noOptionsText={actors.error ? "We couldn't load the list of people" : undefined}
                />
                <MultiSelectCombobox
                    className="audit-filter audit-filter-type"
                    options={selectOptions(OBJECT_TYPE_LABELS)}
                    searchPlaceholder="Anything"
                    value={selected.objectType}
                    onChange={(v) => setFilter({ objectType: listToQuery(v) })}
                />
                <MultiSelectCombobox
                    className="audit-filter audit-filter-source"
                    options={selectOptions(SOURCE_LABELS)}
                    searchPlaceholder="Anywhere"
                    value={selected.source}
                    onChange={(v) => setFilter({ source: listToQuery(v) })}
                />
                {filtersActive && (
                    <button
                        type="button"
                        className="audit-clear-filters"
                        onClick={() => setFilter({ objectType: null, actor: null, source: null, objectId: null })}
                    >
                        Clear filters
                    </button>
                )}
                <div className="audit-log-date">
                    <DateRangePicker range={dateRange} onChange={onDateRangeChanged} />
                </div>
                {query.objectId && (
                    <button type="button" className="audit-scope" onClick={() => setFilter({ objectId: null })}>
                        <LucideIcon iconName="Filter" size={13} />
                        One object&apos;s history
                        <LucideIcon iconName="X" size={13} />
                    </button>
                )}
            </div>

            {error && allData.length === 0 && (
                <div className="audit-log-list audit-log-state">
                    <div className="audit-log-state-title">We couldn&apos;t load the activity log</div>
                    <Button size="medium" variant="outlined" onClick={retry}>
                        Try again
                    </Button>
                </div>
            )}
            {initialLoading && <PlainLoader style={{ width: '4rem', margin: '3rem auto', display: 'block' }} />}
            {isEmpty && (
                <div className="audit-log-list audit-log-state">
                    <LucideIcon iconName="Clock" size={36} className="audit-log-state-icon" />
                    <div className="audit-log-state-title">
                        {filtersActive ? 'No changes match this filter' : 'No changes recorded yet'}
                    </div>
                    {!filtersActive && <p>When someone changes your settings, it will show up here.</p>}
                </div>
            )}
            {allData.length > 0 && (
                <InfiniteTable
                    data={allData}
                    columns={columns}
                    loadingMore={isFetching}
                    pagination={{ next: data?.next, previous: data?.previous }}
                    onLoadMore={loadMore}
                />
            )}
            {error && allData.length > 0 && (
                <div className="audit-log-more-error">
                    We couldn&apos;t load more of the log.
                    <Button size="small" variant="text" onClick={retry}>
                        Try again
                    </Button>
                </div>
            )}
        </div>
    )
}
